import { Fragment } from "react";
import type React from "react";
import { Box, Group, Text } from "@mantine/core";
import { Button, Table } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { RowEdit } from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import { OE_COLUMNS, colSpanOf } from "@utils/resultTableColumns";
import type {
  MappingResultRow,
  MappingResultRowOe,
  TmVariant,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import WdJobResultsTableRow from "./WdJobResultsTableRow";
import formatVariantLabel, { variantIdentity } from "@utils/variantLabel";

interface WdJobResultsTableProps {
  rows: MappingResultRowOe[];
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  jobId: string;
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
  onAccept: (rowIndex: number) => void;
  onReject: (rowIndex: number) => void;
  onRecover: (rowIndex: number) => void;
  onRetrySave: (rowIndex: number) => void;
  onRowRestored: (row: MappingResultRow) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

// Derived from the leaf columns — the OE header groups some of them across
// two rows, and a group cell is not a column.
/** The vehicle variant a row was extracted under — TM only; OE rows have no
 *  variant axis. Rows are grouped under a header whenever it changes. */

const variantOf = (row: MappingResultRow): TmVariant | null =>
  row.kind === MappingResultKind.Tm ? row.source.variant : null;

const COL_SPAN = colSpanOf(OE_COLUMNS);

const WdJobResultsTable: React.FC<WdJobResultsTableProps> = ({
  rows,
  edits,
  saveStates,
  jobId,
  registerRowRef,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
  onRowRestored,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();

  return (
    <Box className="job-results-page__table-wrap job-results-page__table-wrap--wd">
      <Table
        striped
        withTableBorder
        withColumnBorders
        verticalSpacing="sm"
        horizontalSpacing="sm"
        layout="fixed"
        className="job-results-page__table job-results-page__table--wd"
      >
        <colgroup>
          <col style={{ width: "3%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "23%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "19%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "7%" }} />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th rowSpan={2} style={{ width: "3%" }}>
              {t("jobResultsPage.table.rowIndex", { defaultValue: "#" })}
            </Table.Th>
            <Table.Th
              colSpan={4}
              style={{ width: "55%" }}
              className="job-results-page__group-header job-results-page__group-header--source"
            >
              {t("jobResultsPage.wdTable.source", { defaultValue: "Source" })}
            </Table.Th>
            <Table.Th
              colSpan={2}
              style={{ width: "25%" }}
              className="job-results-page__group-header job-results-page__group-header--suggested"
            >
              {t("jobResultsPage.wdTable.suggested", {
                defaultValue: "Suggestion",
              })}
            </Table.Th>
            <Table.Th rowSpan={2} style={{ width: "10%" }}>
              {t("jobResultsPage.table.status", { defaultValue: "Status" })}
            </Table.Th>
            <Table.Th rowSpan={2} style={{ width: "7%" }}>
              {t("jobResultsPage.table.actions", { defaultValue: "Actions" })}
            </Table.Th>
          </Table.Tr>
          <Table.Tr>
            <Table.Th style={{ width: "7%" }}>
              {t("jobResultsPage.wdTable.numberOe", {
                defaultValue: "OE Number",
              })}
            </Table.Th>
            <Table.Th style={{ width: "23%" }}>
              {t("jobResultsPage.wdTable.nameOe", {
                defaultValue: "OE Text",
              })}
            </Table.Th>
            <Table.Th style={{ width: "16%" }}>
              {t("jobResultsPage.wdTable.oeFunction", {
                defaultValue: "OE Function",
              })}
            </Table.Th>
            <Table.Th style={{ width: "9%" }}>
              {t("jobResultsPage.wdTable.oeDomain", {
                defaultValue: "OE Domain",
              })}
            </Table.Th>
            <Table.Th style={{ width: "6%" }}>
              {t("jobResultsPage.wdTable.itemMpId", {
                defaultValue: "ItemMP ID",
              })}
            </Table.Th>
            <Table.Th style={{ width: "19%" }}>
              {t("jobResultsPage.wdTable.itemMpText", {
                defaultValue: "ItemMP Text",
              })}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => {
            const edit = edits.get(row.rowIndex);
            const variant = variantOf(row);
            const prevVariant = idx > 0 ? variantOf(rows[idx - 1]) : null;
            const showVariantHeader =
              variant !== null && variantIdentity(variant) !==
                (prevVariant === null ? null : variantIdentity(prevVariant));
            const saveState = saveStates.get(row.rowIndex);
            return (
              <Fragment key={row.rowIndex}>
                {showVariantHeader && (
                  <Table.Tr className="job-results-page__variant-row">
                    <Table.Td colSpan={COL_SPAN}>
                      <Text className="job-results-page__variant-label">
                        {t("jobResultsPage.table.variant", {
                          defaultValue: "Variant",
                        })}
                        {" · "}
                        {formatVariantLabel(variant)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                <WdJobResultsTableRow
                  row={row}
                  edit={edit}
                  saveState={saveState}
                  jobId={jobId}
                  registerRef={(el) => registerRowRef(row.rowIndex, el)}
                  onAccept={() => onAccept(row.rowIndex)}
                  onReject={() => onReject(row.rowIndex)}
                  onRecover={() => onRecover(row.rowIndex)}
                  onRetrySave={() => onRetrySave(row.rowIndex)}
                  onRowRestored={onRowRestored}
                />
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>

      {hasMore && (
        <Group justify="center" p="sm">
          <Button
            variant="subtle"
            size="sm"
            loading={loadingMore}
            onClick={onLoadMore}
          >
            {t("mappingServicePage.main.loadMore")}
          </Button>
        </Group>
      )}
    </Box>
  );
};

export default WdJobResultsTable;
